import { Input } from '@shared/components/ui';
import {
  Eye,
  EyeOff,
  Key,
  Database,
  Check,
  AlertCircle,
  Loader2,
  Folder,
  X,
  Plus,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderBrowserModal } from '../FolderBrowserModal';

interface CredentialsProps {
  data: {
    type: 'POSTGRESQL' | 'FILES';
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    database?: string;
    databases?: string[];
    usePgDumpAll?: boolean;
    backupGlobalsOnly?: boolean;
    paths?: string[];
    useVSS?: boolean; // Windows Volume Shadow Copy Service
    agent_id?: string;
  };
  onChange: (data: unknown) => void;
  onNext: () => void;
  onBack: () => void;
}

interface TestConnectionResult {
  success: boolean;
  databases?: string[];
  error?: string;
  server_version?: string;
}

export function Credentials({
  data,
  onChange,
  onNext: _onNext,
  onBack: _onBack,
}: CredentialsProps) {
  const { t } = useTranslation('backups');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    host?: string;
    port?: string;
    username?: string;
    password?: string;
    database?: string;
    path?: string;
    connection?: string;
  }>({});

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [isFolderBrowserOpen, setIsFolderBrowserOpen] = useState(false);

  // Check if we have connection details (for edit mode - show database UI even without fresh test)
  const hasConnectionDetails = data.host && data.port && data.username && data.password;

  const testConnection = async () => {
    // Validate connection fields first
    const newErrors: Record<string, string> = {};
    if (!data.host) newErrors.host = t('wizard.credentials.required');
    if (!data.port) newErrors.port = t('wizard.credentials.required');
    if (!data.username) newErrors.username = t('wizard.credentials.required');
    if (!data.password) newErrors.password = t('wizard.credentials.required');
    if (!data.agent_id) newErrors.connection = t('wizard.credentials.agentRequired');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/backups/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          host: data.host,
          port: data.port,
          username: data.username,
          password: data.password,
          agent_id: data.agent_id,
          ssl_mode: 'prefer',
        }),
      });

      const result = await response.json();
      setTestResult(result);

      if (result.success && result.databases) {
        // Auto-select first non-system database by default (single database only)
        const defaultDb = result.databases.find(
          (db: string) => !['postgres', 'template0', 'template1'].includes(db)
        );
        onChange({
          ...data,
          database: defaultDb || '',
          databases: defaultDb ? [defaultDb] : [],
          usePgDumpAll: false,
        });
      }
    } catch (_error) {
      setTestResult({
        success: false,
        error: t('wizard.credentials.networkError'),
      });
    } finally {
      setTesting(false);
    }
  };

  const selectDatabase = (dbName: string) => {
    // Single database selection only
    onChange({
      ...data,
      database: dbName,
      databases: [dbName], // Keep in sync for backend compatibility
    });
    setErrors({ ...errors, database: undefined });
  };

  const clearSelection = () => {
    onChange({
      ...data,
      database: '',
      databases: [],
    });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-2xl flex items-start space-x-3">
        <Key className="text-state-info-light dark:text-state-info-dark shrink-0" size={20} />
        <p className="text-xs font-medium text-state-info-light dark:text-state-info-dark leading-relaxed">
          {data.type === 'POSTGRESQL'
            ? t('wizard.credentials.pgsqlDescription')
            : t('wizard.credentials.filesDescription')}
        </p>
      </div>

      {data.type === 'POSTGRESQL' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('wizard.credentials.hostLabel')}
              placeholder="localhost"
              value={data.host || ''}
              onChange={(e) => {
                onChange({ ...data, host: e.target.value });
                setErrors({ ...errors, host: undefined });
                setTestResult(null);
              }}
              error={errors.host}
            />
            <Input
              label={t('wizard.credentials.portLabel')}
              type="number"
              placeholder="5432"
              value={data.port || ''}
              onChange={(e) => {
                onChange({ ...data, port: parseInt(e.target.value) || undefined });
                setErrors({ ...errors, port: undefined });
                setTestResult(null);
              }}
              error={errors.port}
            />
          </div>

          <Input
            label={t('wizard.credentials.usernameLabel')}
            placeholder="postgres"
            value={data.username || ''}
            onChange={(e) => {
              onChange({ ...data, username: e.target.value });
              setErrors({ ...errors, username: undefined });
              setTestResult(null);
            }}
            error={errors.username}
          />

          <div className="relative">
            <Input
              label={t('wizard.credentials.passwordLabel')}
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={data.password || ''}
              onChange={(e) => {
                onChange({ ...data, password: e.target.value });
                setErrors({ ...errors, password: undefined });
                setTestResult(null);
              }}
              error={errors.password}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Test Connection Button */}
          <button
            type="button"
            onClick={testConnection}
            disabled={testing}
            className="w-full py-2.5 px-4 bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark text-slate-700 dark:text-slate-300 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>{t('wizard.credentials.testing')}</span>
              </>
            ) : (
              <>
                <Database size={18} />
                <span>{t('wizard.credentials.testConnection')}</span>
              </>
            )}
          </button>

          {errors.connection && (
            <p className="text-sm text-state-danger-light dark:text-state-danger-dark flex items-center gap-1">
              <AlertCircle size={14} />
              {errors.connection}
            </p>
          )}

          {/* Connection Result */}
          {testResult && (
            <div
              className={`p-4 rounded-xl border ${
                testResult.success
                  ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20'
                  : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {testResult.success ? (
                  <Check className="text-state-ok-light dark:text-state-ok-dark" size={18} />
                ) : (
                  <AlertCircle
                    className="text-state-danger-light dark:text-state-danger-dark"
                    size={18}
                  />
                )}
                <span
                  className={`font-semibold ${
                    testResult.success
                      ? 'text-state-ok-light dark:text-state-ok-dark'
                      : 'text-state-danger-light dark:text-state-danger-dark'
                  }`}
                >
                  {testResult.success
                    ? t('wizard.credentials.connectionSuccess')
                    : t('wizard.credentials.connectionFailed')}
                </span>
              </div>

              {testResult.success ? (
                <div className="text-sm text-slate-600 dark:text-text-subtle-dark space-y-1">
                  <p>{testResult.server_version}</p>
                  <p>
                    {t('wizard.credentials.foundDatabases', {
                      count: testResult.databases?.length || 0,
                    })}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
                  {testResult.error}
                </p>
              )}
            </div>
          )}

          {/* Backup Mode Selection - Show after successful connection or when editing with existing connection */}
          {(testResult?.success || hasConnectionDetails) && (
            <div className="space-y-4 border-t border-surface-border-light dark:border-surface-border-dark pt-4">
              <h4 className="font-semibold text-slate-900 dark:text-white">
                {t('wizard.credentials.backupMode')}
              </h4>

              <div className="space-y-3">
                <label
                  className={`flex items-start p-4 border rounded-xl cursor-pointer transition-colors ${
                    data.usePgDumpAll
                      ? 'border-state-info-light dark:border-state-info-dark bg-state-info-bg-light dark:bg-state-info-bg-dark'
                      : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                  }`}
                >
                  <input
                    type="radio"
                    name="backupMode"
                    checked={data.usePgDumpAll || false}
                    onChange={() => {
                      onChange({
                        ...data,
                        usePgDumpAll: true,
                        backupGlobalsOnly: false,
                        databases: [],
                      });
                      setErrors({ ...errors, database: undefined });
                    }}
                    className="mt-0.5 mr-3"
                  />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {t('wizard.credentials.backupAll')}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                      {t('wizard.credentials.backupAllDesc')}
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start p-4 border rounded-xl cursor-pointer transition-colors ${
                    data.backupGlobalsOnly
                      ? 'border-state-info-light dark:border-state-info-dark bg-state-info-bg-light dark:bg-state-info-bg-dark'
                      : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                  }`}
                >
                  <input
                    type="radio"
                    name="backupMode"
                    checked={data.backupGlobalsOnly || false}
                    onChange={() => {
                      onChange({
                        ...data,
                        usePgDumpAll: false,
                        backupGlobalsOnly: true,
                        database: '',
                        databases: [],
                      });
                      setErrors({ ...errors, database: undefined });
                    }}
                    className="mt-0.5 mr-3"
                  />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {t('wizard.credentials.backupGlobals')}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                      {t('wizard.credentials.backupGlobalsDesc')}
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start p-4 border rounded-xl cursor-pointer transition-colors ${
                    !data.usePgDumpAll && !data.backupGlobalsOnly
                      ? 'border-state-info-light dark:border-state-info-dark bg-state-info-bg-light dark:bg-state-info-bg-dark'
                      : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                  }`}
                >
                  <input
                    type="radio"
                    name="backupMode"
                    checked={!data.usePgDumpAll && !data.backupGlobalsOnly}
                    onChange={() =>
                      onChange({
                        ...data,
                        usePgDumpAll: false,
                        backupGlobalsOnly: false,
                      })
                    }
                    className="mt-0.5 mr-3"
                  />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {t('wizard.credentials.backupSingle')}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                      {t('wizard.credentials.backupSingleDesc')}
                    </p>
                  </div>
                </label>
              </div>

              {/* Database Selection - Only for "single database" mode */}
              {!data.usePgDumpAll && !data.backupGlobalsOnly && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-slate-900 dark:text-white">
                      {t('wizard.credentials.selectDatabase')}
                    </h5>
                    {data.database && (
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300"
                      >
                        {t('wizard.credentials.clear')}
                      </button>
                    )}
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-surface-border-light dark:border-surface-border-dark rounded-xl divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {/* Show databases from test result if available, otherwise show saved databases */}
                    {(testResult?.databases || data.databases || []).map((db: string) => {
                      const isSystem = ['postgres', 'template0', 'template1'].includes(db);
                      const isSelected = data.database === db;

                      return (
                        <label
                          key={db}
                          className={`flex items-center px-4 py-2.5 cursor-pointer hover:bg-surface-2-light dark:hover:bg-surface-2-dark ${
                            isSelected ? 'bg-state-info-bg-light dark:bg-state-info-bg-dark' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="databaseSelection"
                            checked={isSelected}
                            onChange={() => selectDatabase(db)}
                            className="mr-3"
                          />
                          <span
                            className={`text-sm ${
                              isSystem
                                ? 'text-text-muted-light dark:text-text-muted-dark'
                                : 'text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {db}
                            {isSystem && (
                              <span className="ml-2 text-xs text-text-subtle-light dark:text-text-subtle-dark">
                                {t('wizard.credentials.system')}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Show message when no databases available */}
                  {!testResult?.databases && (!data.databases || data.databases.length === 0) && (
                    <p className="text-sm text-text-muted-light dark:text-text-muted-dark italic">
                      {t('wizard.credentials.clickToLoad')}
                    </p>
                  )}

                  {errors.database && (
                    <p className="text-sm text-state-danger-light dark:text-state-danger-dark flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.database}
                    </p>
                  )}

                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                    {data.database
                      ? t('wizard.credentials.selected', { database: data.database })
                      : t('wizard.credentials.noDatabaseSelected')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Directory Paths Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('wizard.credentials.pathsLabel')}
              </label>
              <button
                type="button"
                onClick={() => setIsFolderBrowserOpen(true)}
                disabled={!data.agent_id}
                className="flex items-center px-3 py-1.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {t('wizard.credentials.browseButton')}
              </button>
            </div>

            {!data.agent_id && (
              <p className="text-xs text-state-warn-light dark:text-state-warn-dark mb-2">
                {t('wizard.credentials.selectAgentFirst')}
              </p>
            )}

            {errors.path && (
              <p className="text-sm text-state-danger-light dark:text-state-danger-dark flex items-center gap-1 mb-2">
                <AlertCircle size={14} />
                {errors.path}
              </p>
            )}

            {/* Selected Paths List */}
            <div
              className={`border border-surface-border-light dark:border-surface-border-dark rounded-xl overflow-hidden bg-surface-2-light dark:bg-surface-2-dark ${!data.paths?.length ? 'min-h-[120px]' : ''}`}
            >
              {!data.paths || data.paths.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-text-muted-light dark:text-text-muted-dark">
                  <Folder className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm font-medium">
                    {t('wizard.credentials.noDirectoriesSelected')}
                  </p>
                  <p className="text-xs">{t('wizard.credentials.clickBrowse')}</p>
                </div>
              ) : (
                <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                  {data.paths.map((path) => (
                    <div
                      key={path}
                      className="flex items-center justify-between p-3 hover:bg-surface-2-light dark:hover:bg-surface-border-dark transition-colors"
                    >
                      <div className="flex items-center min-w-0">
                        <Folder className="h-5 w-5 text-state-warn-light dark:text-state-warn-dark mr-3 shrink-0" />
                        <span
                          className="text-sm text-slate-700 dark:text-slate-300 truncate"
                          title={path}
                        >
                          {path}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newPaths = (data.paths || []).filter((p) => p !== path);
                          onChange({ ...data, paths: newPaths });
                        }}
                        className="ml-2 p-1 text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark rounded transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {data.paths && data.paths.length > 0 && (
              <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
                {data.paths.length === 1
                  ? t('wizard.credentials.pathsSelected', { count: data.paths.length })
                  : t('wizard.credentials.pathsSelectedPlural', { count: data.paths.length })}
              </p>
            )}
          </div>

          {/* VSS Toggle for Windows */}
          <div
            className={`p-4 border rounded-xl transition-colors ${data.useVSS ? 'border-state-info-light dark:border-state-info-dark bg-state-info-bg-light dark:bg-state-info-bg-dark' : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'}`}
          >
            <label htmlFor="backup-config-vss" className="flex items-start cursor-pointer">
              <input
                id="backup-config-vss"
                type="checkbox"
                checked={data.useVSS || false}
                onChange={(e) => {
                  onChange({ ...data, useVSS: e.target.checked });
                }}
                className="mt-0.5 mr-3"
              />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  {t('wizard.credentials.vssTitle')}
                </p>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                  {t('wizard.credentials.vssDescription')}
                </p>
              </div>
            </label>
            {data.useVSS && (
              <div className="mt-3 p-3 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-lg">
                <p className="text-xs font-medium text-state-warn-light dark:text-state-warn-dark">
                  <strong>{t('wizard.credentials.vssWarning')}:</strong>{' '}
                  {t('wizard.credentials.vssWarningDesc')}
                </p>
              </div>
            )}
          </div>

          <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
            <p className="text-xs font-medium text-slate-600 dark:text-text-subtle-dark">
              {t('wizard.credentials.agentScanInfo')}
            </p>
          </div>
        </div>
      )}

      {/* Folder Browser Modal */}
      <FolderBrowserModal
        isOpen={isFolderBrowserOpen}
        onClose={() => setIsFolderBrowserOpen(false)}
        agentId={data.agent_id || ''}
        selectedPaths={data.paths || []}
        onSelectPaths={(newPaths) => {
          onChange({ ...data, paths: newPaths });
          setErrors({ ...errors, path: undefined });
        }}
        allowMultiple={true}
      />
    </div>
  );
}
